import React, { useState, useEffect } from 'react';
import { Box, Grid, Card, CardContent, Typography, Button, Badge, Chip, Paper, IconButton, TextField, Divider, List, ListItem, ListItemText } from '@mui/material';
import QrCodeIcon from '@mui/icons-material/QrCode';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useLanguage } from '../context/LanguageContext';
import { useApp } from '../context/AppContext';

export default function QROrderingView() {
    const { t, currentLanguage } = useLanguage();
    const { tables, setTables, setOrders, showToast, menuItems } = useApp();

    // Dynamic QR generation logic for local Wi-Fi and online demos
    const currentProtocol = window.location.protocol; // 'http:' or 'https:'
    const currentHostname = window.location.hostname;
    const currentPort = window.location.port; // empty if default 80/443
    
    const isLocal = currentHostname === 'localhost' || currentHostname === '127.0.0.1';
    const [localIp, setLocalIp] = useState(isLocal ? '192.168.1.10' : currentHostname);
    
    const portSuffix = currentPort ? `:${currentPort}` : '';
    // Append ?view=menu to redirect mobile scans to the customer ordering page!
    const qrData = `${currentProtocol}//${localIp}${portSuffix}/?view=menu&table=4`;
    const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(qrData)}`;

    const [pendingOrders, setPendingOrders] = useState([]);

    const fetchPendingOrders = async () => {
        if (isLocal) {
            const saved = JSON.parse(localStorage.getItem('qr_orders') || '[]');
            setPendingOrders(saved.filter(o => o.status === 'Pending'));
        } else {
            try {
                const res = await fetch('./sync.php');
                const data = await res.json();
                if (Array.isArray(data)) {
                    setPendingOrders(data.filter(o => o.status === 'Pending'));
                }
            } catch (err) {
                console.error("Error fetching QR orders", err);
            }
        }
    };

    useEffect(() => {
        fetchPendingOrders();
        const interval = setInterval(fetchPendingOrders, 3000);
        return () => clearInterval(interval);
    }, []);

    const handleAcceptQrOrder = async (qrOrder) => {
        const tableNum = qrOrder.table;
        const table = tables.find(t => t.number === tableNum || t.number === parseInt(tableNum));
        const tableId = table ? table.id : 'table_4';

        const orderId = qrOrder.id;
        const subtotal = qrOrder.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        const tax = subtotal * 0.05;

        // Map items to match POS/KDS schema
        const mappedItems = qrOrder.items.map(item => {
            const menuItem = menuItems.find(m => m.name.toLowerCase() === item.name.toLowerCase());
            return {
                id: menuItem ? menuItem.id : 1,
                name: item.name,
                price: item.price,
                quantity: item.quantity,
                remarks: 'QR Customer Order'
            };
        });

        const newKdsOrder = {
            id: orderId,
            tableId: tableId,
            type: `Table ${tableNum}`,
            waiterName: 'Self QR',
            timestamp: new Date().toISOString(),
            status: 'preparing',
            items: mappedItems,
            kots: [{
                id: `KOT-${Math.floor(100 + Math.random() * 900)}`,
                time: new Date().toISOString(),
                items: mappedItems.map(i => ({ id: i.id, quantity: i.quantity, remarks: 'Self Order' }))
            }],
            subtotal,
            tax,
            discount: 0,
            total: subtotal + tax
        };

        // Add order globally to POS system
        setOrders(prev => [...prev, newKdsOrder]);

        // Mark table as occupied
        if (table) {
            setTables(prev => prev.map(t => t.id === tableId ? { ...t, status: 'occupied', activeOrderId: orderId } : t));
        }

        // Show success notification
        showToast(currentLanguage === 'mr' 
            ? `टेबल ${tableNum} चा ऑर्डर स्वीकारला आणि किचन मध्ये पाठवला!` 
            : currentLanguage === 'hi' 
            ? `टेबल ${tableNum} का ऑर्डर स्वीकार किया गया और रसोई में भेजा गया!` 
            : `Table ${tableNum} order accepted & sent to Kitchen!`);

        // Mark order as accepted
        if (isLocal) {
            const saved = JSON.parse(localStorage.getItem('qr_orders') || '[]');
            const updated = saved.map(o => o.id === qrOrder.id ? { ...o, status: 'Accepted' } : o);
            localStorage.setItem('qr_orders', JSON.stringify(updated));
            fetchPendingOrders();
        } else {
            try {
                await fetch(`./sync.php?accept=${qrOrder.id}`);
                fetchPendingOrders();
            } catch (err) {
                console.error("Error accepting QR order", err);
            }
        }
    };

    const handleRejectQrOrder = async (orderId) => {
        if (isLocal) {
            const saved = JSON.parse(localStorage.getItem('qr_orders') || '[]');
            const updated = saved.map(o => o.id === orderId ? { ...o, status: 'Rejected' } : o);
            localStorage.setItem('qr_orders', JSON.stringify(updated));
            fetchPendingOrders();
        } else {
            try {
                await fetch(`./sync.php?accept=${orderId}`); // Mark as accepted/removed on server
                fetchPendingOrders();
            } catch (err) {
                console.error("Error rejecting QR order", err);
            }
        }
        showToast(currentLanguage === 'mr' ? 'ऑर्डर रद्द केला!' : currentLanguage === 'hi' ? 'ऑर्डर रद्द किया!' : 'Order Rejected!');
    };

    return (
        <Box sx={{ p: 4 }}>
            {/* Header Description */}
            <Typography variant="h4" sx={{ fontWeight: 900, color: '#008751', mb: 1 }}>
                {currentLanguage === 'mr' ? 'QR मेनू आणि क्यूआर ऑर्डरिंग' : currentLanguage === 'hi' ? 'QR मेनू और क्यूआर ऑर्डरिंग' : 'QR Menu & Table Ordering'}
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748b', mb: 4 }}>
                {currentLanguage === 'mr'
                    ? 'टेबलवरील QR कोड स्कॅन करून थेट मोबाईलवरून सुरक्षित डिजिटल ऑर्डर द्या.'
                    : currentLanguage === 'hi'
                    ? 'टेबल के QR कोड को स्कैन करके सीधे मोबाइल से सुरक्षित डिजिटल ऑर्डर दें।'
                    : 'Real-time table QR Ordering system. Scan the code with a smartphone to place live orders.'}
            </Typography>

            <Grid container spacing={4} alignItems="flex-start">
                {/* Left Column: QR Code Standee Card */}
                <Grid item xs={12} md={5}>
                    <Card sx={{ 
                        borderRadius: 4, 
                        border: '2px solid #008751', 
                        boxShadow: '0 8px 30px rgba(0, 135, 81, 0.1)', 
                        textAlign: 'center',
                        p: 4
                    }}>
                        <Typography variant="h5" sx={{ fontWeight: 900, color: '#008751', mb: 2 }}>
                            {currentLanguage === 'mr' ? 'स्कॅन करा आणि ऑर्डर करा' : currentLanguage === 'hi' ? 'स्कैन करें और ऑर्डर करें' : 'SCAN & ORDER'}
                        </Typography>
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            p: 3, 
                            bgcolor: '#ffffff', 
                            borderRadius: 3, 
                            border: '2px dashed #10b981',
                            mb: 3
                        }}>
                            <img src={qrImageUrl} alt="Scannable QR Code" style={{ width: 220, height: 220 }} />
                        </Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                            {currentLanguage === 'mr' ? 'टेबल नंबर: ०४' : currentLanguage === 'hi' ? 'टेबल नंबर: 04' : 'Table Number: 04'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 2 }}>
                            {currentLanguage === 'mr'
                                ? 'मोबाईल कॅमेऱ्याने QR स्कॅन करून ॲप डाऊनलोड न करता ऑर्डर द्या.'
                                : currentLanguage === 'hi'
                                ? 'मोबाइल कैमरे से QR स्कैन करके बिना ऐप डाउनलोड किए ऑर्डर दें।'
                                : 'Scan with mobile camera to view our digital menu and order instantly.'}
                        </Typography>
                        <Box sx={{ p: 1.5, mt: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0', textAlign: 'left' }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#475569', display: 'block', mb: 1 }}>
                                🔗 DEMO WI-FI PATH CONFIGURATION
                            </Typography>
                            <TextField
                                size="small"
                                label="Your Laptop IP Address"
                                value={localIp}
                                onChange={(e) => setLocalIp(e.target.value)}
                                helperText={`Generates: ${qrData}`}
                                FormHelperTextProps={{ style: { fontSize: '0.65rem', fontWeight: 700 } }}
                                fullWidth
                                sx={{ bgcolor: '#fff' }}
                            />
                        </Box>
                    </Card>
                </Grid>

                {/* Right Column: Live Incoming QR Orders Dashboard */}
                <Grid item xs={12} md={7}>
                    <Card sx={{ borderRadius: 4, p: 3, minHeight: 450, boxShadow: '0 8px 30px rgba(0,0,0,0.02)', border: '1px solid #e2e8f0' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 900, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                🔔 {currentLanguage === 'mr' ? 'थेट क्यूआर ऑर्डर्स' : currentLanguage === 'hi' ? 'लाइव क्यूआर ऑर्डर्स' : 'Live QR Customer Orders'}
                                {pendingOrders.length > 0 && (
                                    <Chip 
                                        label={pendingOrders.length} 
                                        color="error" 
                                        size="small" 
                                        sx={{ fontWeight: 800, height: 20, animation: 'pulse 1.5s infinite' }} 
                                    />
                                )}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 800 }}>
                                ● {currentLanguage === 'mr' ? 'थेट कनेक्टेड' : currentLanguage === 'hi' ? 'लाइव कनेक्टेड' : 'LIVE RECEIVER'}
                            </Typography>
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        {pendingOrders.length === 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, textAlign: 'center' }}>
                                <Box sx={{ fontSize: 48, mb: 2 }}>📡</Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#64748b', mb: 1 }}>
                                    {currentLanguage === 'mr' ? 'नवीन ऑर्डर्सची प्रतीक्षा आहे...' : currentLanguage === 'hi' ? 'नए ऑर्डर्स की प्रतीक्षा है...' : 'Waiting for scanned orders...'}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#94a3b8', maxWidth: 360 }}>
                                    {currentLanguage === 'mr'
                                        ? 'मोबाईलवरून डावीकडील QR कोड स्कॅन करा, मेनूमधून काही मागवा आणि "ऑर्डर पाठवा" दाबा.'
                                        : currentLanguage === 'hi'
                                        ? 'मोबाइल से बाईं ओर का QR कोड स्कैन करें, मेनू से कुछ चुनें और "ऑर्डर भेजें" दबाएं।'
                                        : 'Scan the QR Code on the left with your phone, add items to the cart, and click "Place Order".'}
                                </Typography>
                            </Box>
                        ) : (
                            <List sx={{ p: 0 }}>
                                {pendingOrders.map((order) => (
                                    <Paper 
                                        key={order.id} 
                                        sx={{ 
                                            p: 2.5, 
                                            mb: 2.5, 
                                            borderRadius: 3, 
                                            border: '1px solid #10b981', 
                                            bgcolor: '#f0fdf4',
                                            boxShadow: 'none' 
                                        }}
                                    >
                                        <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                                            <Grid item>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#008751' }}>
                                                    {currentLanguage === 'mr' ? `टेबल नंबर: ${order.table}` : currentLanguage === 'hi' ? `टेबल नंबर: ${order.table}` : `Table No: ${order.table}`}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
                                                    ID: {order.id} | {order.timestamp}
                                                </Typography>
                                            </Grid>
                                            <Grid item>
                                                <Chip 
                                                    label={`Total: ₹${order.total}`} 
                                                    sx={{ bgcolor: '#008751', color: '#fff', fontWeight: 800 }} 
                                                />
                                            </Grid>
                                        </Grid>

                                        <Divider sx={{ my: 1, borderColor: 'rgba(16, 185, 129, 0.2)' }} />

                                        <Box sx={{ my: 1.5 }}>
                                            {order.items.map((item, idx) => (
                                                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155' }}>
                                                        {item.name} <span style={{ color: '#10b981', fontWeight: 800 }}>x{item.quantity}</span>
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: '#475569', fontWeight: 700 }}>
                                                        ₹{item.price * item.quantity}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>

                                        <Divider sx={{ my: 1.5, borderColor: 'rgba(16, 185, 129, 0.2)' }} />

                                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                startIcon={<CancelIcon />}
                                                onClick={() => handleRejectQrOrder(order.id)}
                                                sx={{ fontWeight: 800, borderRadius: 2 }}
                                            >
                                                {currentLanguage === 'mr' ? 'रद्द करा' : currentLanguage === 'hi' ? 'रद्द करें' : 'Reject'}
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                startIcon={<CheckCircleIcon />}
                                                onClick={() => handleAcceptQrOrder(order)}
                                                sx={{ fontWeight: 800, borderRadius: 2 }}
                                            >
                                                {currentLanguage === 'mr' ? 'स्वीकारा आणि KDS ला पाठवा' : currentLanguage === 'hi' ? 'स्वीकारें और KDS भेजें' : 'Accept & Send to KDS'}
                                            </Button>
                                        </Box>
                                    </Paper>
                                ))}
                            </List>
                        )}
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}
